import React, { useCallback } from 'react';
import { BonusToggle } from 'components/bonus-toggle/BonusToggle';
import { Btn } from 'components/btn/Btn';
import { sortOptions } from './state';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { Modal } from 'components/modal/Modal';

interface DealsFilterSectionProps {
    categories: string[];
    state: any;
    dispatch: any;
    count: number;
}

const DealsFilterMobileSection = ({
    categories = [],
    state,
    dispatch,
    count: _count,
}: DealsFilterSectionProps) => {
    const onPanelDealTypeClick = useCallback(
        (dealType: string) => {
            const nextDeals = state.panelTempOptions.dealTypes.includes(dealType)
                ? state.panelTempOptions.dealTypes.filter((dt: string) => dt !== dealType)
                : [...state.panelTempOptions.dealTypes, dealType];

            dispatch({
                type: 'UPDATE_TEMP_PANEL_OPTIONS',
                payload: {
                    dealTypes: nextDeals,
                },
            });
        },
        [state, dispatch]
    );

    const onPanelCategoryClick = useCallback(
        (category: string) => {
            const nextCategories = state.panelTempOptions.categories.includes(category)
                ? state.panelTempOptions.categories.filter((c: string) => c !== category)
                : [...state.panelTempOptions.categories, category];

            dispatch({
                type: 'UPDATE_TEMP_PANEL_OPTIONS',
                payload: {
                    categories: nextCategories,
                },
            });
        },
        [state, dispatch]
    );

    return (
        <div className="lg:w-[881px] md:mx-auto w-full">
            <div className="w-full border-t border-b border-grey-1 flex justify-around items-center">
                <button
                    className={[
                        'text-grey-1 h-[62px] w-1/2 flex items-center justify-center underline border-r border-r-grey-1',
                    ].join(' ')}
                    onClick={() => dispatch({ type: 'SET_SHOW_PANEL', payload: 'filters' })}
                >
                    Filter
                </button>
                <button
                    className={[
                        'text-grey-1 h-[62px] w-1/2 flex items-center justify-center underline',
                    ].join(' ')}
                    onClick={() => dispatch({ type: 'SET_SHOW_SORT_BY_PANEL', payload: true })}
                >
                    Sort
                </button>
            </div>

            <Modal
                isOpen={state.showSortByPanel}
                onClose={() => dispatch({ type: 'SET_SHOW_SORT_BY_PANEL', payload: false })}
                title=""
            >
                <div className="p-2">
                    {sortOptions.map((option) => (
                        <div key={option.value} className="flex items-center py-2">
                            <input
                                type="radio"
                                id={option.value}
                                value={option.value}
                                checked={state.sortBy.value === option.value}
                                onChange={() => dispatch({ type: 'SET_SORT_BY', payload: option })}
                            />
                            <label className="ml-2 text-brand" htmlFor={option.value}>
                                {option.label}
                            </label>
                        </div>
                    ))}
                </div>
            </Modal>

            <Modal
                isOpen={state.showPanel !== ''}
                title=""
                onClose={() => {
                    dispatch({ type: 'SET_SHOW_PANEL', payload: '' });
                    dispatch({ type: 'CANCEL_PANEL' });
                }}
            >
                <>
                    {(state.categories.length > 0 || state.dealTypes.length > 0) && (
                        <div className="w-full flex flex-row flex-wrap align-baseline mt-2">
                            <button
                                className="font-semibold text-brand text-[12px] pr-2 underline"
                                onClick={() => {
                                    dispatch({ type: 'CLEAR_ALL_DEAL_TYPES' });
                                    dispatch({ type: 'CLEAR_ALL_CATEGORIES' });
                                    dispatch({
                                        type: 'UPDATE_TEMP_PANEL_OPTIONS',
                                        payload: {
                                            categories: [],
                                            dealTypes: [],
                                        },
                                    });
                                }}
                            >
                                Clear All
                            </button>

                            <ul className="flex flex-row flex-wrap">
                                {state.dealTypes.map((dealType: string) => (
                                    <li key={dealType}>
                                        <button
                                            className="font-semibold text-brand text-[12px] mr-2 rounded-full px-2 py-1 bg-pale-blue mb-1"
                                            onClick={() => {
                                                dispatch({
                                                    type: 'REMOVE_DEAL_TYPE',
                                                    payload: dealType,
                                                });
                                            }}
                                        >
                                            <span className="mr-1">{dealType}</span>
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-brand-dark inset-0 m-auto"
                                            />
                                        </button>
                                    </li>
                                ))}
                                {state.categories.map((category: string) => (
                                    <li key={category}>
                                        <button
                                            className="font-semibold text-brand text-[12px] mr-2 rounded-full px-2 py-1 bg-pale-blue mb-1"
                                            onClick={() => {
                                                dispatch({
                                                    type: 'REMOVE_CATEGORY',
                                                    payload: category,
                                                });
                                            }}
                                        >
                                            <span className="mr-1">{category}</span>
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-brand-dark inset-0 m-auto"
                                            />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex flex-col">
                        {state.showPanel === 'filters' && (
                            <div className="flex flex-col">
                                <h3 className="font-semibold text-[16px] text-center text-grey-darkest">
                                    Deal Type
                                </h3>

                                <div className="grid grid-cols-2 gap-4 w-full flex-grow px-2 pb-4 pt-1">
                                    {['Online Coupon', 'Online Deal'].map((dealType) => (
                                        <button
                                            key={dealType}
                                            className={[
                                                'px-2 py-2 border border-brand rounded-[8px] text-[12px] text-center min-h-[56px]',
                                                state.panelTempOptions.dealTypes.includes(dealType)
                                                    ? 'text-dark-blue bg-pale-blue'
                                                    : 'text-brand',
                                            ].join(' ')}
                                            onClick={() => {
                                                onPanelDealTypeClick(dealType);
                                            }}
                                        >
                                            {dealType}
                                        </button>
                                    ))}
                                </div>

                                <h3 className="font-semibold text-[16px] text-center text-grey-darkest">
                                    Categories
                                </h3>

                                <div className="grid grid-cols-2 gap-4 w-full flex-grow px-2 pb-4 pt-1">
                                    {categories.map((category) => (
                                        <button
                                            key={category}
                                            className={[
                                                'px-2 py-2 border border-brand rounded-[8px] text-[12px] text-center min-h-[56px]',
                                                state.panelTempOptions.categories.includes(category)
                                                    ? 'text-dark-blue bg-pale-blue'
                                                    : 'text-brand',
                                            ].join(' ')}
                                            onClick={() => {
                                                onPanelCategoryClick(category);
                                            }}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex flex-row justify-center px-2">
                                    <BonusToggle
                                        currentBonusState={state.panelTempOptions.viewBonusOnly}
                                        toggleFunc={() => {
                                            dispatch({
                                                type: 'UPDATE_TEMP_PANEL_OPTIONS',
                                                payload: {
                                                    ...state.panelTempOptions,
                                                    viewBonusOnly:
                                                        !state.panelTempOptions.viewBonusOnly,
                                                },
                                            });
                                        }}
                                    />
                                </div>

                                <div className="flex flex-col justify-center gap-2 items-center w-full">
                                    <Btn
                                        onClick={() => {
                                            dispatch({ type: 'CONFIRM_PANEL' });
                                        }}
                                    >
                                        Show Results
                                    </Btn>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            </Modal>
        </div>
    );
};

export default DealsFilterMobileSection;
